import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import settingsService from '../../../services/settingsService';

const InterfaceCustomization = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [brandingSettings, setBrandingSettings] = useState({
    companyName: 'Enterprise Corp',
    logoUrl: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200&h=80&fit=crop',
    primaryColor: '#2563EB',
    secondaryColor: '#64748B',
    accentColor: '#0EA5E9',
    favicon: '/favicon.ico',
    customCSS: ''
  });

  const [layoutSettings, setLayoutSettings] = useState({
    sidebarCollapsed: false,
    defaultView: 'grid',
    itemsPerPage: 25,
    showBreadcrumbs: true,
    showQuickActions: true,
    compactMode: false,
    darkModeEnabled: false,
    highContrastMode: false
  });

  const [localizationSettings, setLocalizationSettings] = useState({
    defaultLanguage: 'en',
    defaultTimezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12',
    numberFormat: 'US',
    currencySymbol: '$'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    showDesktopNotifications: true,
    soundEnabled: true,
    emailDigest: true,
    digestFrequency: 'daily',
    notificationPosition: 'top-right'
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Load settings from Supabase on component mount
  useEffect(() => {
    if (user?.id) {
      loadSettings();
    }
  }, [user?.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      const settings = await settingsService?.getSettings(user?.id, 'interface');
      
      // Update state with loaded settings
      if (settings?.branding) setBrandingSettings(settings?.branding);
      if (settings?.layout) setLayoutSettings(settings?.layout);
      if (settings?.localization) setLocalizationSettings(settings?.localization);
      if (settings?.notifications) setNotificationSettings(settings?.notifications);
      
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  };

  const viewOptions = [
    { value: 'grid', label: 'Grid View' },
    { value: 'list', label: 'List View' },
    { value: 'table', label: 'Table View' }
  ];

  const itemsPerPageOptions = [
    { value: 10, label: '10 items' },
    { value: 25, label: '25 items' },
    { value: 50, label: '50 items' },
    { value: 100, label: '100 items' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'zh', label: 'Chinese' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'GMT' },
    { value: 'Europe/Paris', label: 'CET' },
    { value: 'Asia/Tokyo', label: 'JST' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY' }
  ];

  const timeFormatOptions = [
    { value: '12', label: '12-hour (AM/PM)' },
    { value: '24', label: '24-hour' }
  ];

  const digestFrequencyOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
  ];

  const notificationPositionOptions = [
    { value: 'top-right', label: 'Top Right' },
    { value: 'top-left', label: 'Top Left' },
    { value: 'bottom-right', label: 'Bottom Right' },
    { value: 'bottom-left', label: 'Bottom Left' }
  ];

  const handleBrandingChange = (field, value) => {
    setBrandingSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleLayoutChange = (field, value) => {
    setLayoutSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleLocalizationChange = (field, value) => {
    setLocalizationSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleNotificationChange = (field, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Save all settings categories
      await settingsService?.upsertMultipleSettings(user?.id, 'interface', {
        branding: brandingSettings,
        layout: layoutSettings,
        localization: localizationSettings,
        notifications: notificationSettings
      });

      setHasChanges(false);
      setSuccessMessage('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setBrandingSettings({
      companyName: 'Enterprise Corp',
      logoUrl: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200&h=80&fit=crop',
      primaryColor: '#2563EB',
      secondaryColor: '#64748B',
      accentColor: '#0EA5E9',
      favicon: '/favicon.ico',
      customCSS: ''
    });
    setLayoutSettings({
      sidebarCollapsed: false,
      defaultView: 'grid',
      itemsPerPage: 25,
      showBreadcrumbs: true,
      showQuickActions: true,
      compactMode: false,
      darkModeEnabled: false,
      highContrastMode: false
    });
    setLocalizationSettings({
      defaultLanguage: 'en',
      defaultTimezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12',
      numberFormat: 'US',
      currencySymbol: '$'
    });
    setNotificationSettings({
      showDesktopNotifications: true,
      soundEnabled: true,
      emailDigest: true,
      digestFrequency: 'daily',
      notificationPosition: 'top-right'
    });
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="text-primary animate-spin" />
        <span className="ml-3 text-muted-foreground">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive" />
          <span className="text-sm text-destructive">{error}</span>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
          <Icon name="CheckCircle" size={20} className="text-success" />
          <span className="text-sm text-success">{successMessage}</span>
        </div>
      )}

      {/* Branding Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Palette" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Branding & Theme</h3>
            <p className="text-sm text-muted-foreground">Customize the visual appearance and branding</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="Company Name"
              value={brandingSettings?.companyName}
              onChange={(e) => handleBrandingChange('companyName', e?.target?.value)}
              description="Displayed in header and login screen"
            />

            <Input
              label="Logo URL"
              value={brandingSettings?.logoUrl}
              onChange={(e) => handleBrandingChange('logoUrl', e?.target?.value)}
              description="URL to company logo image"
            />

            <Input
              label="Favicon URL"
              value={brandingSettings?.favicon}
              onChange={(e) => handleBrandingChange('favicon', e?.target?.value)}
              description="Browser tab icon"
            />
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandingSettings?.primaryColor}
                    onChange={(e) => handleBrandingChange('primaryColor', e?.target?.value)}
                    className="w-12 h-10 rounded border border-border cursor-pointer"
                  />
                  <Input
                    value={brandingSettings?.primaryColor}
                    onChange={(e) => handleBrandingChange('primaryColor', e?.target?.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandingSettings?.secondaryColor}
                    onChange={(e) => handleBrandingChange('secondaryColor', e?.target?.value)}
                    className="w-12 h-10 rounded border border-border cursor-pointer"
                  />
                  <Input
                    value={brandingSettings?.secondaryColor}
                    onChange={(e) => handleBrandingChange('secondaryColor', e?.target?.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Accent Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandingSettings?.accentColor}
                    onChange={(e) => handleBrandingChange('accentColor', e?.target?.value)}
                    className="w-12 h-10 rounded border border-border cursor-pointer"
                  />
                  <Input
                    value={brandingSettings?.accentColor}
                    onChange={(e) => handleBrandingChange('accentColor', e?.target?.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Live Preview</h4>
          <div className="flex items-center gap-3 p-3 bg-background border border-border rounded">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: brandingSettings?.primaryColor }}
            >
              <Icon name="FileText" size={16} color="white" />
            </div>
            <span className="font-semibold text-foreground">{brandingSettings?.companyName}</span>
          </div>
        </div>
      </div>
      {/* Layout & Display Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Layout" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Layout & Display</h3>
            <p className="text-sm text-muted-foreground">Configure default interface layout and behavior</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Default View Mode"
              description="How documents are displayed by default"
              options={viewOptions}
              value={layoutSettings?.defaultView}
              onChange={(value) => handleLayoutChange('defaultView', value)}
            />

            <Select
              label="Items Per Page"
              description="Number of items shown per page"
              options={itemsPerPageOptions}
              value={layoutSettings?.itemsPerPage}
              onChange={(value) => handleLayoutChange('itemsPerPage', value)}
            />

            <Checkbox
              label="Collapse sidebar by default"
              description="Start with sidebar in collapsed state"
              checked={layoutSettings?.sidebarCollapsed}
              onChange={(e) => handleLayoutChange('sidebarCollapsed', e?.target?.checked)}
            />

            <Checkbox
              label="Show breadcrumb navigation"
              description="Display navigation breadcrumbs"
              checked={layoutSettings?.showBreadcrumbs}
              onChange={(e) => handleLayoutChange('showBreadcrumbs', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Show quick action buttons"
              description="Display quick action toolbar"
              checked={layoutSettings?.showQuickActions}
              onChange={(e) => handleLayoutChange('showQuickActions', e?.target?.checked)}
            />

            <Checkbox
              label="Compact mode"
              description="Reduce spacing for more content"
              checked={layoutSettings?.compactMode}
              onChange={(e) => handleLayoutChange('compactMode', e?.target?.checked)}
            />

            <Checkbox
              label="Enable dark mode"
              description="Use dark theme by default"
              checked={layoutSettings?.darkModeEnabled}
              onChange={(e) => handleLayoutChange('darkModeEnabled', e?.target?.checked)}
            />

            <Checkbox
              label="High contrast mode"
              description="Increase contrast for accessibility"
              checked={layoutSettings?.highContrastMode}
              onChange={(e) => handleLayoutChange('highContrastMode', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Localization Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Globe" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Localization & Formats</h3>
            <p className="text-sm text-muted-foreground">Configure regional settings and display formats</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Default Language"
              description="System-wide default language"
              options={languageOptions}
              value={localizationSettings?.defaultLanguage}
              onChange={(value) => handleLocalizationChange('defaultLanguage', value)}
            />

            <Select
              label="Default Timezone"
              description="Default timezone for new users"
              options={timezoneOptions}
              value={localizationSettings?.defaultTimezone}
              onChange={(value) => handleLocalizationChange('defaultTimezone', value)}
            />

            <Select
              label="Date Format"
              description="How dates are displayed"
              options={dateFormatOptions}
              value={localizationSettings?.dateFormat}
              onChange={(value) => handleLocalizationChange('dateFormat', value)}
            />
          </div>

          <div className="space-y-4">
            <Select
              label="Time Format"
              description="12-hour or 24-hour time display"
              options={timeFormatOptions}
              value={localizationSettings?.timeFormat}
              onChange={(value) => handleLocalizationChange('timeFormat', value)}
            />

            <Input
              label="Currency Symbol"
              value={localizationSettings?.currencySymbol}
              onChange={(e) => handleLocalizationChange('currencySymbol', e?.target?.value)}
              description="Symbol used for currency display"
            />

            <div className="p-3 bg-muted rounded-lg">
              <h4 className="text-sm font-medium text-foreground mb-2">Format Preview</h4>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Date: {new Date()?.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: localizationSettings?.dateFormat?.includes('MMM') ? 'short' : '2-digit',
                  day: '2-digit'
                })}</p>
                <p>Time: {new Date()?.toLocaleTimeString('en-US', { 
                  hour12: localizationSettings?.timeFormat === '12',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
                <p>Currency: {localizationSettings?.currencySymbol}1,234.56</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Notification Preferences Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notification Preferences</h3>
            <p className="text-sm text-muted-foreground">Configure system-wide notification settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable desktop notifications"
              description="Show browser notifications for important events"
              checked={notificationSettings?.showDesktopNotifications}
              onChange={(e) => handleNotificationChange('showDesktopNotifications', e?.target?.checked)}
            />

            <Checkbox
              label="Enable notification sounds"
              description="Play sound alerts for notifications"
              checked={notificationSettings?.soundEnabled}
              onChange={(e) => handleNotificationChange('soundEnabled', e?.target?.checked)}
            />

            <Checkbox
              label="Enable email digest"
              description="Send periodic email summaries"
              checked={notificationSettings?.emailDigest}
              onChange={(e) => handleNotificationChange('emailDigest', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <Select
              label="Email Digest Frequency"
              description="How often to send email summaries"
              options={digestFrequencyOptions}
              value={notificationSettings?.digestFrequency}
              onChange={(value) => handleNotificationChange('digestFrequency', value)}
              disabled={!notificationSettings?.emailDigest}
            />

            <Select
              label="Notification Position"
              description="Where to show desktop notifications"
              options={notificationPositionOptions}
              value={notificationSettings?.notificationPosition}
              onChange={(value) => handleNotificationChange('notificationPosition', value)}
              disabled={!notificationSettings?.showDesktopNotifications}
            />
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <span className="text-sm font-medium text-warning">You have unsaved changes</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset} disabled={saving}>
              Reset Changes
            </Button>
            <Button 
              variant="default" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Interface Settings'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterfaceCustomization;